import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Trash2, UserCheck, Paperclip, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";
import { ProductFields, emptyProduct, cleanProduct, productProfit } from "@/components/ProductFields";
import api, { fmt, errText } from "@/lib/api";

const emptyInfo = () => ({
  order_number: "", customer_id: "", customer_name: "", phone: "", address: "",
  order_date: format(new Date(), "yyyy-MM-dd"), delivery_date: "",
});

export default function OrderForm({ open, onOpenChange }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [info, setInfo] = useState(emptyInfo());
  const [products, setProducts] = useState([emptyProduct()]);
  const [advance, setAdvance] = useState("");
  const [pdfs, setPdfs] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const suggestRef = useRef(null);
  const fileRef = useRef(null);

  const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data, enabled: open });
  const { data: karigars } = useQuery({ queryKey: ["karigars"], queryFn: async () => (await api.get("/karigars")).data, enabled: open });

  useEffect(() => {
    const h = (e) => { if (suggestRef.current && !suggestRef.current.contains(e.target)) setShowSuggest(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const nameCounts = useMemo(() => {
    const m = {};
    (customers || []).forEach((c) => { const k = (c.name || "").trim().toLowerCase(); m[k] = (m[k] || 0) + 1; });
    return m;
  }, [customers]);

  const suggestions = (customers || []).filter((c) => {
    const q = info.customer_name.trim().toLowerCase();
    return q && !info.customer_id && (c.name.toLowerCase().includes(q) || (c.phone || "").includes(q));
  }).slice(0, 5);

  const pickCustomer = (c) => {
    setInfo({ ...info, customer_id: c.id, customer_name: c.name, phone: c.phone || "", address: c.address || "" });
    setShowSuggest(false);
  };

  const create = useMutation({
    mutationFn: async () => {
      const { data: order } = await api.post("/orders", {
        customer_id: info.customer_id || null,
        customer_name: info.customer_name,
        phone: info.phone,
        address: info.address,
        order_number: info.order_number,
        order_date: info.order_date,
        delivery_date: info.delivery_date,
        advance: Number(advance) || 0,
        products: products.filter((p) => p.name.trim()).map(cleanProduct),
      });
      if (pdfs.length > 0) {
        const fd = new FormData();
        pdfs.forEach((f) => fd.append("files", f));
        await api.post(`/orders/${order.id}/attachments`, fd);
      }
      return order;
    },
    onSuccess: (d) => {
      toast.success(`Order ${d.order_number} created`);
      ["orders", "customers", "karigars", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      reset();
      onOpenChange(false);
      navigate(`/orders/${d.id}`);
    },
    onError: (e) => toast.error(errText(e)),
  });

  const reset = () => { setInfo(emptyInfo()); setProducts([emptyProduct()]); setAdvance(""); setPdfs([]); };
  const total = products.reduce((s, p) => s + (Number(p.customer_price) || 0) * (Number(p.quantity) || 1), 0);
  const profit = products.reduce((s, p) => s + productProfit(p), 0);
  const remaining = Math.max(total - (Number(advance) || 0), 0);
  const canSave = info.customer_name.trim() && products.some((p) => p.name.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">New Order</DialogTitle></DialogHeader>
        <div className="space-y-5 pt-2">
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Order Information</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Order Number</Label>
                <Input
                  data-testid="order-number-input"
                  value={info.order_number}
                  onChange={(e) => setInfo({ ...info, order_number: e.target.value })}
                  placeholder="Leave blank for auto (AN-…)"
                  className="mt-1"
                />
              </div>
              <div className="relative col-span-2 sm:col-span-1" ref={suggestRef}>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Customer Name *</Label>
                <Input
                  data-testid="order-customer-name-input"
                  value={info.customer_name}
                  onChange={(e) => { setInfo({ ...info, customer_name: e.target.value, customer_id: "" }); setShowSuggest(true); }}
                  onFocus={() => setShowSuggest(true)}
                  placeholder="Type name..."
                  className="mt-1"
                />
                {info.customer_id && (
                  <span className="absolute right-2 top-8 text-emerald-500" title="Existing customer"><UserCheck className="h-4 w-4" /></span>
                )}
                {showSuggest && suggestions.length > 0 && (
                  <div data-testid="customer-suggestions" className="absolute z-50 top-full mt-1 left-0 right-0 rounded-lg border bg-popover shadow-lg overflow-hidden">
                    {suggestions.map((c) => (
                      <button key={c.id} type="button" data-testid={`customer-suggestion-${c.id}`} onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors">
                        {c.name}{nameCounts[(c.name || "").trim().toLowerCase()] > 1 && c.address ? <span className="text-amber-500"> — {c.address}</span> : null} {c.phone && <span className="text-muted-foreground">· {c.phone}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Phone</Label>
                <Input data-testid="order-customer-phone-input" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} placeholder="+91..." className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Address</Label>
                <Input data-testid="order-customer-address-input" value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} placeholder="Address" className="mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Order Date</Label>
                <div className="mt-1"><DateField testId="order-date-field" value={info.order_date} onChange={(v) => setInfo({ ...info, order_date: v })} /></div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Delivery Date</Label>
                <div className="mt-1"><DateField testId="delivery-date-field" value={info.delivery_date} onChange={(v) => setInfo({ ...info, delivery_date: v })} /></div>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Attach PDF</Label>
                <input ref={fileRef} type="file" accept=".pdf,application/pdf" multiple className="hidden" data-testid="order-pdf-input"
                  onChange={(e) => { setPdfs([...pdfs, ...Array.from(e.target.files || [])]); e.target.value = ""; }} />
                <div className="mt-1 space-y-2">
                  <Button type="button" variant="outline" size="sm" data-testid="attach-pdf-button" className="rounded-lg" onClick={() => fileRef.current?.click()}>
                    <Paperclip className="h-3.5 w-3.5 mr-1.5" /> Attach PDF
                  </Button>
                  {pdfs.map((f, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border px-3 py-1.5 text-sm">
                      <span className="truncate flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-red-400 shrink-0" />{f.name}</span>
                      <Button type="button" variant="ghost" size="icon" data-testid={`remove-pdf-${i}`} className="h-6 w-6 text-muted-foreground" onClick={() => setPdfs(pdfs.filter((_, idx) => idx !== i))}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Products</p>
            {products.map((p, i) => (
              <div key={i} className="rounded-xl border p-4 relative">
                {products.length > 1 && (
                  <Button variant="ghost" size="icon" data-testid={`remove-product-${i}`} className="absolute top-2 right-2 h-7 w-7 text-muted-foreground z-10" onClick={() => setProducts(products.filter((_, idx) => idx !== i))}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
                <ProductFields idx={i} value={p} karigars={karigars} onChange={(v) => setProducts(products.map((x, idx) => (idx === i ? v : x)))} />
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="add-product-line-button" className="rounded-lg" onClick={() => setProducts([...products, emptyProduct()])}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Product
            </Button>
          </div>

          <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Payment</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</p>
                <p data-testid="order-total-display" className="font-heading text-lg font-semibold">{fmt(total)}</p>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Advance ₹</Label>
                <Input data-testid="order-advance-input" type="number" min="0" value={advance} onChange={(e) => setAdvance(e.target.value)} className="mt-1" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Remaining</p>
                <p data-testid="order-remaining-display" className={`font-heading text-lg font-semibold ${remaining > 0 ? "text-red-500" : "text-green-600"}`}>{fmt(remaining)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Profit</p>
                <p data-testid="order-profit-display" className={`font-heading text-lg font-semibold ${profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{fmt(profit)}</p>
              </div>
            </div>
          </div>

          <Button
            data-testid="order-save-button"
            className="w-full h-11 rounded-lg active:scale-95 transition-transform"
            disabled={!canSave || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Saving..." : "Save Order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
